import { Injectable } from '@angular/core';

/** The three tools a broker's firm can lock settings on, kept apart because their titles overlap. */
type FilterPreferenceTool = 'product' | 'affordability' | 'criteria';

/**
 * Which filters the broker's firm has locked for the current search.
 *
 * Its own service because four unrelated places need the same answer - the search form fields, the results filter
 * panel, the results grid and the affordability outcomes screen - and they are reached at different points in the
 * journey. Holding it on any one of them would mean the others depending on it for something that is not its job.
 *
 * Scoped per tool rather than one flat set of titles. "Affordability met" is a filter on both the Product search
 * and the Affordability search, with a separate lock flag on each, so a single set would make locking one of them
 * silently lock the other.
 *
 * Presentation only. The resolved preferences already carry the firm's value for a locked slot, and the API
 * re-applies every lock on save, because a client is never trusted with the enforcement.
 */
@Injectable({ providedIn: 'root' })
export class FilterPreferenceLocksService {
  private readonly locked = new Map<FilterPreferenceTool, ReadonlySet<string>>();

  public setLockedGroups(tool: FilterPreferenceTool, lockedGroups: ReadonlySet<string>): void {
    this.locked.set(tool, lockedGroups);
  }

  public isLocked(tool: FilterPreferenceTool, filterTitle: string): boolean {
    return this.locked.get(tool)?.has(filterTitle) ?? false;
  }

  public lockedGroups(tool: FilterPreferenceTool): ReadonlySet<string> {
    return this.locked.get(tool) ?? new Set<string>();
  }

  /** Cleared when a search journey ends, so a broker whose firm has since unlocked a filter is not still held to it. */
  public reset(): void {
    this.locked.clear();
  }
}
